import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyWorkItemFilters } from '../../../components/work-items/applyWorkItemFilters';
import type { GroupBy, WorkItemFilters } from '../../../components/work-items/types';
import { supabase } from '../../../lib/supabase';
import {
  applyBoardDragOver,
  cardsNeedPersist,
  computeDropPersistence
} from './boardDnd';
import { boardCollisionDetection } from './boardCollision';
import { BoardColumn } from './BoardColumn';
import { BoardCardPreview } from './BoardCardPreview';
import { chipValueFor } from './chipUtils';
import { BOARD_CHIP_KEYS, BOARD_FALLBACK_STAGES, type BoardWorkItemRow } from './types';
import './board.css';

type LoadState = 'loading' | 'ready' | 'error';

export function WorkItemsBoardTab({
  filters,
  groupBy,
  visibleCols,
  activeOrgId,
  projectId,
  onOpenItem,
  reloadSignal
}: {
  filters: WorkItemFilters;
  groupBy: GroupBy;
  visibleCols: ReadonlyArray<string>;
  activeOrgId: string | null;
  /** When set, scope the board to a single project's work items. */
  projectId?: string;
  onOpenItem: (id: string) => void;
  reloadSignal: number;
}) {
  const [cards, setCards] = useState<ReadonlyArray<BoardWorkItemRow>>([]);
  const [stages, setStages] = useState<ReadonlyArray<string>>(
    BOARD_FALLBACK_STAGES
  );
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragSnapshotRef = useRef<ReadonlyArray<BoardWorkItemRow> | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Hold in 'loading' until we know which org to scope to — without an
    // active org we can't constrain the queries and must not leak cross-org
    // rows (RLS is membership-based and allows all of the user's orgs).
    if (!activeOrgId) {
      setState('loading');
      return;
    }
    setState('loading');
    setError(null);

    // Scope to a single project when `projectId` is set (Project detail board);
    // otherwise stay org-wide (Work Items page board).
    let itemsQuery = supabase
      .from('work_items')
      .select(
        'id, name, type, status, rank, parent_work_item_id, due_date, created_at, created_by, updated_at, assignee_id, priority'
      )
      .eq('org_id', activeOrgId)
      .neq('type', 'subtask');
    if (projectId) itemsQuery = itemsQuery.eq('project_id', projectId);

    void (async () => {
      const [pipelineRes, itemsRes] = await Promise.all([
        supabase
          .from('work_item_status_pipelines')
          .select('stages')
          .eq('org_id', activeOrgId)
          .is('applies_to_type', null)
          .limit(1)
          .maybeSingle(),
        itemsQuery.order('rank', { ascending: true })
      ]);
      if (cancelled) return;
      if (pipelineRes.error) {
        setError(pipelineRes.error.message);
        setState('error');
        return;
      }
      if (itemsRes.error) {
        setError(itemsRes.error.message);
        setState('error');
        return;
      }
      const pipelineStages = pipelineRes.data?.stages;
      if (Array.isArray(pipelineStages) && pipelineStages.length >= 4) {
        setStages(
          (pipelineStages.slice(0, 4) as ReadonlyArray<unknown>).map(String)
        );
      } else {
        setStages(BOARD_FALLBACK_STAGES);
      }
      setCards((itemsRes.data ?? []) as BoardWorkItemRow[]);
      setState('ready');
    })();

    return () => {
      cancelled = true;
    };
    // `reloadSignal` bumps when the editor cover closes → refetch in place.
  }, [activeOrgId, projectId, reloadSignal]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const visibleChipKeys = useMemo(
    () => BOARD_CHIP_KEYS.filter((k) => visibleCols.includes(k)),
    [visibleCols]
  );

  const parentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cards) map.set(c.id, c.name);
    return map;
  }, [cards]);

  const visibleCards = useMemo(() => {
    const withoutEpics = cards.filter((c) => c.type !== 'epic');
    return applyWorkItemFilters(withoutEpics, filters);
  }, [cards, filters]);

  const visibleIds = useMemo(
    () => new Set(visibleCards.map((c) => c.id)),
    [visibleCards]
  );

  const dndOptions = useMemo(
    () => ({ groupBy, visibleIds }),
    [groupBy, visibleIds]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(String(event.active.id));
      dragSnapshotRef.current = cards;
    },
    [cards]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;
      setCards((prev) => {
        const next = applyBoardDragOver(
          prev,
          String(active.id),
          String(over.id),
          dndOptions
        );
        return next ?? prev;
      });
    },
    [dndOptions]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    const snapshot = dragSnapshotRef.current;
    dragSnapshotRef.current = null;
    if (snapshot) setCards(snapshot);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const cardId = String(event.active.id);
      const snapshot = dragSnapshotRef.current;
      dragSnapshotRef.current = null;

      if (!snapshot) return;

      setCards((current) => {
        const persisted = computeDropPersistence(current, cardId, dndOptions);
        if (!persisted) return snapshot;

        const before = snapshot.find((c) => c.id === cardId);
        if (!before || !cardsNeedPersist(before, persisted)) return snapshot;

        const optimistic = current.map((c) =>
          c.id === cardId
            ? { ...c, status: persisted.status, rank: persisted.rank }
            : c
        );

        void (async () => {
          const { error: updateErr } = await supabase.rpc(
            'set_work_item_status',
            {
              p_work_item_id: cardId,
              p_status: persisted.status,
              p_rank: persisted.rank
            }
          );
          if (updateErr) {
            setCards(snapshot);
            setError(updateErr.message);
          }
        })();

        return optimistic;
      });
    },
    [dndOptions]
  );

  const changeStatus = useCallback(
    (cardId: string, nextStatus: string) => {
      setCards((current) => {
        const before = current.find((c) => c.id === cardId);
        if (!before || before.status === nextStatus) return current;
        const snapshot = current;
        const optimistic = current.map((c) =>
          c.id === cardId ? { ...c, status: nextStatus } : c
        );

        void (async () => {
          const { error: updateErr } = await supabase.rpc(
            'set_work_item_status',
            {
              p_work_item_id: cardId,
              p_status: nextStatus
            }
          );
          if (updateErr) {
            setCards(snapshot);
            setError(updateErr.message);
          }
        })();

        return optimistic;
      });
    },
    []
  );

  const cardsByStatus = useMemo(() => {
    const map = new Map<string, BoardWorkItemRow[]>();
    for (const stage of stages) map.set(stage, []);
    for (const c of visibleCards) {
      const list = map.get(c.status);
      if (list) list.push(c);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.rank - b.rank);
    }
    return map;
  }, [visibleCards, stages]);

  // Edge scroll controls — the columns overflow horizontally, so surface a
  // ‹ / › button at each edge (visible on hover) that pages the scroll, and
  // gate each one on whether there's room to scroll that way.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateEdges();
    // ResizeObserver is absent in jsdom (and very old browsers) — degrade to
    // the window resize listener alone rather than throwing on mount.
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateEdges)
        : null;
    ro?.observe(el);
    window.addEventListener('resize', updateEdges);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updateEdges);
    };
    // Recompute when the column/card set changes the scrollable width.
  }, [updateEdges, state, stages.length, visibleCards.length]);
  const scrollByPage = useCallback((dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 240), behavior: 'smooth' });
  }, []);

  return (
    <div
      className="work-items-route__board-scroller"
      data-testid="work-items-route-board-scroller"
    >
      <button
        type="button"
        className="work-items-route__board-arrow work-items-route__board-arrow--left"
        aria-label="Scroll columns left"
        data-testid="work-items-route-board-scroll-left"
        data-visible={edges.left || undefined}
        tabIndex={edges.left ? 0 : -1}
        onClick={() => scrollByPage(-1)}
      >
        <span className="work-items-route__board-arrow-badge" aria-hidden="true">
          <ChevronLeftIcon />
        </span>
      </button>
      <div
        className="work-items-route__board-wrap"
        data-testid="work-items-route-board-wrap"
        data-state={state}
        ref={scrollRef}
        onScroll={updateEdges}
      >
      {state === 'error' ? (
        <div className="work-items-route__error" role="alert">
          Failed to load Board: {error}
        </div>
      ) : (
        <div
          className="work-items-route__board"
          data-testid="work-items-route-board"
          data-state={state}
          data-column-count={stages.length}
          data-visible-chips={visibleChipKeys.join(',')}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={boardCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            {stages.map((stage) => (
              <BoardColumn
                key={stage}
                stage={stage}
                cards={cardsByStatus.get(stage) ?? []}
                groupBy={groupBy}
                visibleChipKeys={visibleChipKeys}
                parentNameById={parentNameById}
                stages={stages}
                onOpen={(card) => onOpenItem(card.id)}
                onParentOpen={(parentId) => onOpenItem(parentId)}
                onChangeStatus={changeStatus}
              />
            ))}
            <DragOverlay dropAnimation={null}>
              {activeId
                ? (() => {
                    const c = visibleCards.find((x) => x.id === activeId);
                    if (!c) return null;
                    const chips = visibleChipKeys
                      .map((k) => ({
                        key: k,
                        value: chipValueFor(
                          k,
                          c,
                          c.parent_work_item_id
                            ? parentNameById.get(c.parent_work_item_id) ?? null
                            : null
                        )
                      }))
                      .filter((x) => x.value != null);
                    return <BoardCardPreview card={c} chips={chips} />;
                  })()
                : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
      </div>
      <button
        type="button"
        className="work-items-route__board-arrow work-items-route__board-arrow--right"
        aria-label="Scroll columns right"
        data-testid="work-items-route-board-scroll-right"
        data-visible={edges.right || undefined}
        tabIndex={edges.right ? 0 : -1}
        onClick={() => scrollByPage(1)}
      >
        <span className="work-items-route__board-arrow-badge" aria-hidden="true">
          <ChevronRightIcon />
        </span>
      </button>
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
